import { useEffect, useMemo, useRef, useState } from "react";
import { createBooking } from "../../api";

// Keep in sync with backend/src/bookings.js
const FIRST_SLOT = 7 * 60;
const LAST_SLOT = 16 * 60 + 30;
const SLOT_MINUTES = 30;
const MAX_GUESTS = 12;
const MAX_DAYS_AHEAD = 60;

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  date: "",
  time: "",
  guests: 2,
  note: "",
  website: "",
};

function pad(n) {
  return String(n).padStart(2, "0");
}

function toDateValue(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function getTimeSlots(dateValue) {
  const now = new Date();
  const isToday = dateValue === toDateValue(now);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const slots = [];

  for (let m = FIRST_SLOT; m <= LAST_SLOT; m += SLOT_MINUTES) {
    if (isToday && m <= nowMinutes) continue;
    slots.push(`${pad(Math.floor(m / 60))}:${pad(m % 60)}`);
  }

  return slots;
}

// Today, or tomorrow once today's last slot has passed.
function getDefaultDate() {
  const today = new Date();
  if (getTimeSlots(toDateValue(today)).length > 0) return toDateValue(today);
  today.setDate(today.getDate() + 1);
  return toDateValue(today);
}

function getEmptyForm() {
  return { ...EMPTY_FORM, date: getDefaultDate() };
}

function BookingModal({ open, onClose, t, lang }) {
  const [form, setForm] = useState(getEmptyForm);
  const [status, setStatus] = useState("idle");
  const [errorText, setErrorText] = useState("");
  const firstInputRef = useRef(null);

  const minDate = toDateValue(new Date());
  const maxDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + MAX_DAYS_AHEAD);
    return toDateValue(d);
  }, []);
  const timeSlots = useMemo(() => (form.date ? getTimeSlots(form.date) : []), [form.date]);
  const guests = form.guests;

  function setGuests(value) {
    const next = Math.min(MAX_GUESTS, Math.max(1, value));
    setForm((current) => ({ ...current, guests: next }));
  }

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    firstInputRef.current?.focus();
    // The page may have stayed open since an earlier day.
    setForm((current) =>
      getTimeSlots(current.date).length > 0 && current.date >= toDateValue(new Date())
        ? current
        : { ...current, date: getDefaultDate(), time: "" },
    );

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open && status === "success") {
      setForm(getEmptyForm());
      setStatus("idle");
    }
  }, [open, status]);

  if (!open) return null;

  function update(field) {
    return (event) => {
      const value = event.target.value;
      setForm((current) => {
        const next = { ...current, [field]: value };
        if (field === "date" && !getTimeSlots(value).includes(current.time)) {
          next.time = "";
        }
        return next;
      });
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!form.time) {
      setStatus("error");
      setErrorText(t.timeRequired);
      return;
    }

    setStatus("loading");
    setErrorText("");

    try {
      await createBooking({ ...form, lang });
      setStatus("success");
    } catch (error) {
      setStatus("error");
      if (error.message === "too_many_requests") setErrorText(t.errorTooMany);
      else if (error.message === "invalid_time" || error.message === "invalid_date") {
        setErrorText(t.errorTime);
      } else setErrorText(t.error);
    }
  }

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) onClose();
  }

  const loading = status === "loading";

  return (
    <div className="newsletter-modal-backdrop" onClick={handleBackdropClick} role="presentation">
      <div
        className="newsletter-modal booking-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-modal-title"
      >
        <button
          type="button"
          className="newsletter-modal-close"
          onClick={onClose}
          aria-label={t.closeLabel}
        >
          ×
        </button>

        <h2 id="booking-modal-title" className="newsletter-modal-title">
          {status === "success" ? t.successTitle : t.title}
        </h2>

        {status === "success" ? (
          <p className="newsletter-modal-subtitle booking-modal-success" role="status">
            {t.successText}
          </p>
        ) : (
          <>
            <p className="newsletter-modal-subtitle">{t.subtitle}</p>

            <form className="booking-form" onSubmit={handleSubmit} lang={lang}>
              <label className="booking-field booking-field--full">
                <span>{t.nameLabel}</span>
                <input
                  ref={firstInputRef}
                  type="text"
                  name="name"
                  required
                  maxLength={120}
                  autoComplete="name"
                  className="newsletter-modal-input"
                  value={form.name}
                  onChange={update("name")}
                  disabled={loading}
                />
              </label>

              <label className="booking-field">
                <span>{t.emailLabel}</span>
                <input
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  className="newsletter-modal-input"
                  value={form.email}
                  onChange={update("email")}
                  disabled={loading}
                />
              </label>

              <label className="booking-field">
                <span>{t.phoneLabel}</span>
                <input
                  type="tel"
                  name="phone"
                  required
                  autoComplete="tel"
                  pattern="[+0-9][0-9 ()/\-]{5,}"
                  className="newsletter-modal-input"
                  value={form.phone}
                  onChange={update("phone")}
                  disabled={loading}
                />
              </label>

              <label className="booking-field">
                <span>{t.dateLabel}</span>
                <input
                  type="date"
                  name="date"
                  required
                  min={minDate}
                  max={maxDate}
                  className="newsletter-modal-input"
                  value={form.date}
                  onChange={update("date")}
                  disabled={loading}
                />
              </label>

              <div className="booking-field">
                <span id="booking-guests-label">{t.guestsLabel}</span>
                <div
                  className="booking-stepper"
                  role="group"
                  aria-labelledby="booking-guests-label"
                >
                  <button
                    type="button"
                    className="booking-stepper-btn"
                    onClick={() => setGuests(guests - 1)}
                    disabled={loading || guests <= 1}
                    aria-label={t.guestsDecrease}
                  >
                    −
                  </button>
                  <output className="booking-stepper-value" aria-live="polite">
                    {t.guestsValue(guests)}
                  </output>
                  <button
                    type="button"
                    className="booking-stepper-btn"
                    onClick={() => setGuests(guests + 1)}
                    disabled={loading || guests >= MAX_GUESTS}
                    aria-label={t.guestsIncrease}
                  >
                    +
                  </button>
                </div>
                {guests >= MAX_GUESTS ? (
                  <small className="booking-hint">{t.largeGroupHint}</small>
                ) : null}
              </div>

              <div className="booking-field booking-field--full">
                <span id="booking-time-label">{t.timeLabel}</span>
                {!form.date ? (
                  <p className="booking-slots-empty">{t.pickDateFirst}</p>
                ) : timeSlots.length === 0 ? (
                  <p className="booking-slots-empty">{t.noSlotsLeft}</p>
                ) : (
                  <div
                    className="booking-slots"
                    role="radiogroup"
                    aria-labelledby="booking-time-label"
                  >
                    {timeSlots.map((slot) => {
                      const selected = form.time === slot;
                      return (
                        <button
                          key={slot}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          className={`booking-slot${selected ? " is-selected" : ""}`}
                          onClick={() => {
                            setForm((current) => ({ ...current, time: slot }));
                            if (status === "error") setStatus("idle");
                          }}
                          disabled={loading}
                        >
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <label className="booking-field booking-field--full">
                <span>{t.noteLabel}</span>
                <textarea
                  name="note"
                  rows={3}
                  maxLength={1000}
                  className="newsletter-modal-input booking-textarea"
                  value={form.note}
                  onChange={update("note")}
                  disabled={loading}
                />
              </label>

              {/* Honeypot for bots — hidden from people and screen readers. */}
              <div className="booking-honeypot" aria-hidden="true">
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={update("website")}
                />
              </div>

              {status === "error" && (
                <p className="newsletter-modal-error booking-field--full" role="alert">
                  {errorText}
                </p>
              )}

              <button
                type="submit"
                className="newsletter-modal-submit booking-field--full"
                disabled={loading}
              >
                {loading ? "..." : t.submit}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default BookingModal;
